import 'core-js/modules/es.object.assign.js';
import { __rest } from 'tslib';
import { jsx, jsxs } from 'react/jsx-runtime';
import React from 'react';
import { Tab } from '@mui/base/Tab';
import classNames from 'classnames';
import '@cognite/cogs-icons';
import '@cognite/cogs-utils';
import '../../tokens/color-tokens/index.js';
import '../../tokens/elevation-tokens/index.js';
import 'core-js/modules/es.array.concat.js';
import '@mui/joy';
import 'core-js/modules/es.array.filter.js';
import 'core-js/modules/es.array.join.js';
import 'core-js/modules/es.object.to-string.js';
import { Tooltip } from '../tooltip/tooltip.js';
import 'core-js/modules/es.array.map.js';
import '@mui/base/Button';
import { OverflowTooltip } from '../_technical/overflow-tooltip/overflow-tooltip.js';

/**
 * SegmentedControl.Button
 * =============================================================================
 * With the SegmentedControl, you can filter, sort or make a display choice,
 * like toggle between different views of the same dataset.
 *
 * [Open docs »](https://cognitedata.github.io/cogs.js/?path=/docs/components-segmentedcontrol--base)
 *
 * -----------------------------------------------------------------------------
 * Examples
 * -----------------------------------------------------------------------------
 * ```
 *  <SegmentedControl>
 *    <SegmentedControl.Button key="1">Histogram</SegmentedControl.Button>
 *    <SegmentedControl.Button key="2">Graph view</SegmentedControl.Button>
 *    <SegmentedControl.Button key="3">List view</SegmentedControl.Button>
 *  </SegmentedControl>
 * ```
 *
 * -----------------------------------------------------------------------------
 * Properties
 * -----------------------------------------------------------------------------
 *
 * @param tooltipContent    - This text will be shown in a tooltip when the button is hovered.
 * @param tooltipPlacement  - Position of the tooltip.
 * @param disabled          - Disable the button and the tooltip if applicable.
 *
 * [Read the full documentation »](https://cognitedata.github.io/cogs.js/?path=/docs/components-segmentedcontrol--base)
 */
var SegmentedControlButton = function SegmentedControlButton(_a) {
  var tooltipContent = _a.tooltipContent,
    _tabKey = _a._tabKey,
    children = _a.children,
    disabled = _a.disabled,
    _a$tooltipPlacement = _a.tooltipPlacement,
    tooltipPlacement = _a$tooltipPlacement === undefined ? 'bottom' : _a$tooltipPlacement,
    icon = _a.icon,
    active = _a.active,
    _a$iconPlacement = _a.iconPlacement,
    iconPlacement = _a$iconPlacement === undefined ? 'left' : _a$iconPlacement,
    className = _a.className,
    onClick = _a.onClick,
    rest = __rest(_a, ["tooltipContent", "_tabKey", "children", "disabled", "tooltipPlacement", "icon", "active", "iconPlacement", "className", "onClick"]);
  var TabContent = function TabContent() {
    return jsxs(Tab, Object.assign({
      className: classNames('cogs-v10', 'cogs-segmented-control__list__button', disabled && 'cogs-segmented-control__list__button--disabled', !React.Children.count(children) && 'cogs-segmented-control__list__button--icon-only', className),
      disabled: disabled,
      "aria-hidden": disabled,
      value: _tabKey,
      role: "tab",
      "data-testid": "segmented-control-button",
      "aria-selected": active
    }, rest, {
      onMouseDown: onClick,
      children: [icon && iconPlacement === 'left' && /*#__PURE__*/React.cloneElement(icon, {
        className: classNames('cogs-v10', 'cogs-segmented-control__list__button__icon', 'cogs-segmented-control__list__button__icon--left')
      }), children && jsx("span", {
        className: "cogs-v10 cogs-segmented-control__list__button__text",
        children: jsx(OverflowTooltip, {
          children: children
        })
      }), icon && iconPlacement === 'right' && /*#__PURE__*/React.cloneElement(icon, {
        className: classNames('cogs-v10', 'cogs-segmented-control__list__button__icon', 'cogs-segmented-control__list__button__icon--right')
      })]
    }));
  };
  if (tooltipContent) {
    return jsx(Tooltip, {
      content: tooltipContent,
      placement: tooltipPlacement,
      children: jsx(TabContent, {})
    });
  }
  return jsx(TabContent, {});
};

export { SegmentedControlButton };
